import type { JSX } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

import {
  FIELD_TYPES,
  type FieldType,
  type UesFieldDef,
} from "../../domain/entities/doc-type";
import { canNest, type FieldPath } from "../schema-editor-ops";
import { wordCount, type FieldError } from "../schema-validation";
import { EnumValuesEditor } from "./enum-values-editor";

export interface FieldRowProps {
  field: UesFieldDef;
  path: FieldPath;
  errors: FieldError[];
  onPatch: (path: FieldPath, patch: Partial<UesFieldDef>) => void;
  onRemove: (path: FieldPath) => void;
  onAddChild: (path: FieldPath) => void;
}

export function FieldRow({
  field,
  path,
  errors,
  onPatch,
  onRemove,
  onAddChild,
}: FieldRowProps): JSX.Element {
  const pathKey = JSON.stringify(path);
  const rowErrors = errors.filter(
    (error) => JSON.stringify(error.path) === pathKey,
  );
  const errorsFor = (fieldName: FieldError["field"]) =>
    rowErrors.filter((error) => error.field === fieldName);
  const inputId = (name: string) => `${name}-${path.join("-")}`;

  const handleTypeChange = (nextType: FieldType) => {
    const patch: Partial<UesFieldDef> = { type: nextType };
    if (nextType !== "object") {
      patch.fields = null;
    }
    if (nextType !== "enum") {
      patch.enumValues = null;
    }
    onPatch(path, patch);
  };

  const renderErrors = (fieldName: FieldError["field"]) =>
    errorsFor(fieldName).map((error, index) => (
      <p
        className="text-xs text-destructive"
        key={`${fieldName}-${index}`}
      >
        {error.message}
      </p>
    ));

  return (
    <div className="space-y-3 rounded-lg border p-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor={inputId("name")}>Name</Label>
          <Input
            id={inputId("name")}
            value={field.name}
            aria-invalid={errorsFor("name").length > 0}
            onChange={(event) => onPatch(path, { name: event.target.value })}
          />
          {renderErrors("name")}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={inputId("label")}>Label</Label>
          <Input
            id={inputId("label")}
            value={field.label}
            aria-invalid={errorsFor("label").length > 0}
            onChange={(event) => onPatch(path, { label: event.target.value })}
          />
          {renderErrors("label")}
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={inputId("type")}>Type</Label>
          <select
            id={inputId("type")}
            value={field.type}
            className="h-8 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
            onChange={(event) =>
              handleTypeChange(event.target.value as FieldType)
            }
          >
            {FIELD_TYPES.filter(
              (type) =>
                type !== "object" || canNest(path) || field.type === "object",
            ).map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <Switch
            id={inputId("required")}
            checked={field.required}
            onCheckedChange={(checked) => onPatch(path, { required: checked })}
          />
          <Label htmlFor={inputId("required")}>Required</Label>
        </div>

        <div className="flex items-center gap-2">
          <Switch
            id={inputId("list")}
            checked={field.isList}
            onCheckedChange={(checked) => onPatch(path, { isList: checked })}
          />
          <Label htmlFor={inputId("list")}>List</Label>
        </div>

        <div className="space-y-1.5 sm:col-span-2 lg:col-span-3">
          <div className="flex items-center justify-between gap-4">
            <Label htmlFor={inputId("description")}>Description</Label>
            <span className="text-xs text-muted-foreground">
              {wordCount(field.description)}/100 words
            </span>
          </div>
          <Input
            id={inputId("description")}
            value={field.description}
            aria-invalid={errorsFor("description").length > 0}
            onChange={(event) =>
              onPatch(path, { description: event.target.value })
            }
          />
          {renderErrors("description")}
        </div>
      </div>

      {field.type === "enum" && (
        <div>
          <EnumValuesEditor
            value={field.enumValues ?? []}
            onChange={(enumValues) => onPatch(path, { enumValues })}
          />
          {renderErrors("enumValues")}
        </div>
      )}

      {field.type === "object" && (
        <div className="space-y-3 border-l pl-4">
          {(field.fields ?? []).map((child, index) => (
            <FieldRow
              key={index}
              field={child}
              path={[...path, index]}
              errors={errors}
              onPatch={onPatch}
              onRemove={onRemove}
              onAddChild={onAddChild}
            />
          ))}
          {renderErrors("fields")}
          {canNest(path) && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onAddChild(path)}
            >
              Add sub-field
            </Button>
          )}
        </div>
      )}

      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-label={`Remove field ${field.name || path.join(".")}`}
        onClick={() => onRemove(path)}
      >
        Remove
      </Button>
    </div>
  );
}
